import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import {
  animations,
  styles,
  Hero,
  SectionHeader,
  Toast,
} from "../../../components/landing/SharedComponent";
import api from "../../../utils/api";

const Contact = () => {
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      details: ["+62 21 1234 5678", "+62 812 3456 7890"],
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: ["pt.adhimaju@gmail.com", "customer@adhimaju.com"],
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Office Address",
      details: [
        "Jl. Cilengkrang 1 No.99B",
        "RT.001 RW.005 Kel. Cisurupan,",
        "Kec. Cibiru, Bandung",
        "Indonesia",
      ],
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Business Hours",
      details: ["Monday - Friday: 08:00 - 17:00", "Saturday: 09:00 - 13:00"],
    },
  ];

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // cegah klik dobel

    const name = e.target[0].value;
    const email = e.target[1].value;
    const message = e.target[2].value;

    setLoading(true); // mulai loading

    try {
      const { data } = await api.post("/api/email/send", {
        name,
        email,
        message,
      });

      if (data.success) {
        setToast({
          show: true,
          message: "Pesan berhasil dikirim!",
          type: "success",
        });
        e.target.reset();
      } else {
        setToast({
          show: true,
          message: `Gagal mengirim: ${data.error || "Unknown error"}`,
          type: "error",
        });
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setToast({
          show: true,
          message: `Error server: ${err.response.data.error}`,
          type: "error",
        });
      } else {
        setToast({
          show: true,
          message: `Error jaringan: ${err.message}`,
          type: "error",
        });
      }
    } finally {
      setLoading(false); // selesai loading
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <Hero
        title="Contact Us"
        subtitle="Get in touch with our team for any inquiries"
        backgroundImage="/images/landing/bg.png"
      />

      <div className={`${styles.container} ${styles.sectionSpacing}`}>
        {/* Map Section */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={animations.fadeInUp}
          className="mb-16"
        >
          <div
            className={`bg-white rounded-lg ${styles.shadowLarge} p-4 h-[600px]`}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.9640003156446!2d107.62790607441946!3d-6.894909567471444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e68e7afb35c0175%3A0x8c444a5775c610b0!2sJl.%20Batik%20Jogja%20No.32%2C%20Sukaluyu%2C%20Kec.%20Cibeunying%20Kaler%2C%20Kota%20Bandung%2C%20Jawa%20Barat%2040123!5e0!3m2!1sid!2sid!4v1755004518083!5m2!1sid!2sid"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-lg"
            />
          </div>
        </motion.div>

        {/* Contact Info Cards */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={animations.staggerChildren}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {contactInfo.map((info, index) => (
            <motion.div
              key={index}
              variants={animations.fadeInUp}
              className={`bg-white rounded-lg p-6 ${styles.shadowBase}`}
            >
              <div className="text-primary mb-4">{info.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {info.title}
              </h3>
              {info.details.map((detail, idx) => (
                <p key={idx} className="text-gray-600 mb-1">
                  {detail}
                </p>
              ))}
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={animations.fadeInUp}
          className="max-w-2xl mx-auto"
        >
          <div className={`bg-white rounded-lg ${styles.shadowLarge} p-8`}>
            <SectionHeader title="Send us a Message" className="mb-8" />
            <form className={styles.elementSpacing} onSubmit={handleSubmit}>
              <div>
                <label className="block text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Message</label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows="4"
                  placeholder="Enter your message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full ${
                  styles.button
                } text-white py-3 rounded-lg font-semibold ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
